<template>
  <div>
    <div class="d-flex mb-3 align-items-end">
      <h1 class="h4 primary--text">
        {{ $t('views.queues_list.queues_list') }}
      </h1>
    </div>
    <v-form
      class="filter white mb-5 pa-6"
      @submit.prevent="showCreateMessageModal"
    >
      <div class="d-flex">
        <div class="input-block input-block_white mr-4">
          <label class="input-block__label">{{ $t('views.queues_list.select_queue') }}</label>
          <v-select
            v-model="workerId"
            :loading="loading && !workerId"
            :items="worker"
            item-text="id"
            item-value="id"
            solo
            hide-details
          />
        </div>
        <div class="input-block input-block_white check-label">
          <label class="input-block__label">show dead</label>
          <v-checkbox
            v-model="showDead"
            :disabled="!workerId"
          />
        </div>
        <div class="input-block input-block_white mr-4 w-auto">
          <v-btn
            class="ml-auto mt-10 mr-10"
            :disabled="!workerId"
            prepend-icon="mdi-toggle-switch"
            @click="toggleMessageFilter"
          >
            <template v-slot:prepend>
              <v-icon color="success" />
            </template>

            {{ messageState }}
          </v-btn>
        </div>
        <div class="input-block input-block_white">
          <label class="input-block__label">{{ $t('views.log_list.path') }}</label>
          <v-text-field
            v-model="pathFilter"
            :hint="`{ \&quot;payload.prop\&quot;: \&quot;value\&quot; }`"
            solo
            hide-details
            @keydown.enter="getWorkerQueue"
          />
        </div>
        <v-btn
          class="ml-auto mt-10 mr-10"
          outlined
          type="submit"
          large
          :disabled="!workerId || isDeadQueue"
        >
          {{ $t('common.create') }}
        </v-btn>
      </div>
    </v-form>
    <v-data-table
      v-if="workerId"
      fixed-header
      :headers="headers"
      :items="workerQueue"
      :footer-props="{
        itemsPerPageOptions: [10, 20, 50, 100],
      }"
      :options.sync="options"
      :server-items-length="total"
      :loading="loading"
      :loading-text="$t('common.loading_data')"
    >
      <template v-slot:item.actions="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-hover v-slot:default="{ hover }">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-on="on"
                @click="showDetailInfoModal(item)"
              >
                mdi-information-outline
              </v-icon>
            </v-hover>
          </template>
          <span>Detail info</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-hover v-slot:default="{ hover }">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-on="on"
                @click="rescheduleItem(item)"
              >
                mdi-update
              </v-icon>
            </v-hover>
          </template>
          <span>Reschedule</span>
        </v-tooltip>
        <v-tooltip
          v-if="item.deleted"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-hover v-slot:default="{ hover }">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-on="on"
                @click="restartMessage(item)"
              >
                mdi-repeat-once
              </v-icon>
            </v-hover>
          </template>
          <span>Reset last try</span>
        </v-tooltip>
        <v-tooltip
          v-if="!item.deleted"
          bottom
        >
          <template v-slot:activator="{ on }">
            <v-hover v-slot:default="{ hover }">
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-on="on"
                @click="forceComplete(item)"
              >
                mdi-check-circle-outline
              </v-icon>
            </v-hover>
          </template>
          <span>Force complete</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-hover
              v-slot:default="{ hover }"
            >
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-on="on"
                @click="moveDeadMessage(item)"
              >
                mdi-skull-outline
              </v-icon>
            </v-hover>
          </template>
          <span>Move dead</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-hover
              v-if="!item.deleted"
              v-slot:default="{ hover }"
            >
              <v-icon
                class="mr-2"
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                v-on="on"
                @click="removeMessage(item)"
              >
                mdi-delete
              </v-icon>
            </v-hover>
          </template>
          <span>Remove</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <detail-info-modal
      v-model="isDetailInfoModalShown"
      :worker-id="workerId"
      :detail-info="detailInfo"
      :is-creation="isMessageCreation"
      :is-reschedule="isReschedule"
      @create="createMessage"
    />
  </div>
</template>

<script>
import DetailInfoModal from '@/components/queues/DetailInfoModal.vue';

export default {
  name: 'QueuesList',

  components: {
    DetailInfoModal,
  },

  data() {
    return {
      total: 0,
      workerQueue: [],
      loading: false,
      options: {
        sortBy: ['createdAt'],
        sortDesc: [true],
        page: 1,
      },

      headers: [
        {
          text: 'visible',
          value: 'visible',
          sortable: false,
        },
        {
          text: 'tries',
          value: 'tries',
          sortable: false,
        },
        {
          text: 'deleted',
          value: 'deleted',
          sortable: false,
        },
        {
          text: this.$t('common.actions'),
          value: 'actions',
          sortable: false,
          width: 150,
          align: 'center',
        },
      ],
      isDetailInfoModalShown: false,
      detailInfo: null,
      isMessageCreation: false,
      isReschedule: false,
      worker: [],
      workerId: null,
      showDead: false,
      messageActive: null,
      pathFilter: '',
    };
  },

  computed: {
    isDeadQueue() {
      return this.worker.find((it) => it.id === this.workerId).queueOptions.deadQueue === 'dead';
    },
    messageState() {
      switch (this.messageActive) {
        case true:
          return 'Only active';
        case false:
          return 'Only deleted';
        default:
          return 'All messages';
      }
    },
  },

  watch: {
    options: {
      handler() {
        if (this.loading) {
          return;
        }
        this.getWorkerQueue();
      },
      deep: true,
    },
    workerId() {
      this.getWorkerQueue();
    },
    showDead() {
      this.getWorkerQueue();
    },
  },

  created() {
    this.getWorker();
  },

  methods: {
    toggleMessageFilter() {
      switch (this.messageActive) {
        case true:
          this.messageActive = false;
          break;
        case false:
          this.messageActive = null;
          break;
        default:
          this.messageActive = true;
      }

      this.getWorkerQueue();
    },
    async getWorker() {
      this.loading = true;

      const params = {};
      params.pageSize = 10000;
      params.page = 1;

      const { data } = await this.$http.get('queue-broker/worker', { params }).finally(() => {
        this.loading = false;
      });

      this.worker = data.rows.sort((a, b) => a.id.localeCompare(b.id));
    },

    async getWorkerQueue() {
      this.loading = true;
      const url = `queue-broker/worker/${this.workerId}/${this.showDead ? 'queue-dead' : 'queue'}`;

      const {
        page, itemsPerPage,
      } = this.options;

      const params = {
        sort: '-visible',
        pageSize: itemsPerPage || 10,
        page,
      };

      params.query = (this.messageActive === null)
        ? {} : { deleted: { $exists: !this.messageActive } };

      if (this.pathFilter) {
        let pathQuery = {};
        try {
          pathQuery = JSON.parse(this.pathFilter);
        } catch (e) {
          pathQuery = {};
        }

        Object.assign(params.query, pathQuery);
      }

      const { data } = await this.$http.get(url, { params }).finally(() => {
        this.loading = false;
      });

      this.workerQueue = data.items.map((it) => ({
        ...it,
        visible: it.visible && this.$moment(it.visible).format('L LT'),
        deleted: it.deleted && this.$moment(it.deleted).format('L LT'),
      }));
      this.total = data.total;
      return Promise.resolve();
    },

    showDetailInfoModal(detailInfo) {
      this.isMessageCreation = false;
      this.isReschedule = false;
      this.isDetailInfoModalShown = true;
      this.detailInfo = detailInfo.payload;
    },

    async removeMessage(item) {
      this.loading = true;
      await this.$http.delete(`queue-broker/worker/${this.workerId}/message/${item.id}`).finally(() => {
        this.loading = false;
      });
      this.getWorkerQueue();
      this.$bvToast.toast(unescape('Message deleted'), {
        title: this.$t('toast.success'),
        variant: 'success',
        solid: true,
        autoHideDelay: 15000,
      });
    },

    async restartMessage(item) {
      this.loading = true;
      await this.$http.post(`queue-broker/worker/${this.workerId}/message/${item.id}/restart`).finally(() => {
        this.loading = false;
      });
      this.getWorkerQueue();
      this.$bvToast.toast(unescape('Message restet last try'), {
        title: this.$t('toast.success'),
        variant: 'success',
        solid: true,
        autoHideDelay: 15000,
      });
    },

    async moveDeadMessage(item) {
      this.loading = true;
      await this.$http.post(`queue-broker/worker/${this.workerId}/message/${item.id}/dead`).finally(() => {
        this.loading = false;
      });
      this.getWorkerQueue();
      this.$bvToast.toast(unescape('Message moved to dead'), {
        title: this.$t('toast.success'),
        variant: 'success',
        solid: true,
        autoHideDelay: 15000,
      });
    },
    async forceComplete(item) {
      this.loading = true;
      await this.$http.post(`queue-broker/worker/${this.workerId}/message/${item.id}/finish`).finally(() => {
        this.loading = false;
      });
      this.getWorkerQueue();
      this.$bvToast.toast(unescape('Message completed'), {
        title: this.$t('toast.success'),
        variant: 'success',
        solid: true,
        autoHideDelay: 15000,
      });
    },
    rescheduleItem(detailInfo) {
      this.isMessageCreation = true;
      this.isReschedule = true;
      this.isDetailInfoModalShown = true;
      this.detailInfo = detailInfo.payload;
    },
    showCreateMessageModal() {
      this.isMessageCreation = true;
      this.isReschedule = false;
      this.isDetailInfoModalShown = true;
    },
    async createMessage(message) {
      await this.$http.post(`queue-broker/worker/${this.workerId}/createMessage`, { message });
      await this.getWorkerQueue().finally(() => {
        this.isDetailInfoModalShown = false;
      });
      this.$bvToast.toast(unescape('Message created'), {
        title: this.$t('toast.success'),
        variant: 'success',
        solid: true,
        autoHideDelay: 15000,
      });
    },
  },
};
</script>
